import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { observer } from 'mobx-react-lite';
import PageInnerContainer from 'pages/components/pageInnerContainer';
import { Typography } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { SectionModel } from 'entities/section/model/sectionModel';
import { FolderModel } from 'entities/folder/model/folderModel';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import CommonSearchResults from 'pages/searchPage/commonSearchResults';
import EntitySearchResults from 'pages/searchPage/entitySearchResults';
import { SortingValue } from 'shared/lib/components/controls/sorting/sortingControl';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash-es';

export default observer(function SearchPage() {
  const { t } = useTranslation();
  const {
    spacings: { sp },
  } = useContext(UiContext);
  const navigate = useNavigate();
  const { mobxStore } = useContext(MobxContext);

  const { searchSectionId, searchFolderId, search, sort, only_direct_match } =
    useSearchParamsUtils();

  const [searchParams, setSearchParams] = useSearchParams();

  const sectionModel = useMemo(() => {
    if (searchSectionId) {
      const newModel = new SectionModel(searchSectionId);
      newModel.fetch({ in_trash: null });

      return newModel;
    }
  }, [searchSectionId]);

  const folderModel = useMemo(() => {
    if (searchFolderId) {
      const newModel = new FolderModel(searchFolderId);
      newModel.fetch({ in_trash: null });

      return newModel;
    }
  }, [searchFolderId]);

  const debouncedGetSearch = useRef(
    debounce(
      (params: {
        search: string;
        searchFolderId?: string | null;
        searchSectionId?: string | null;
        sort: SortingValue;
        search_exact: boolean;
      }) => {
        mobxStore.searchStore.getSearch(params);
      },
      500
    )
  );

  useEffect(() => {
    debouncedGetSearch.current({
      search: search || '',
      searchFolderId,
      searchSectionId,
      sort: sort as SortingValue,
      search_exact: Boolean(only_direct_match),
    });
  }, [
    search,
    searchFolderId,
    searchSectionId,
    mobxStore.searchStore,
    sort,
    only_direct_match,
  ]);

  useEffect(() => {
    return () => {
      mobxStore.searchStore.dropResults();
    };
  }, [mobxStore.searchStore]);

  const onSortingChange = useCallback(
    (newSort: SortingValue) => {
      setSearchParams((curr) => {
        curr.set('sort', newSort || SortingValue.CREATING_DEC);
        return curr;
      });
    },
    [setSearchParams]
  );

  // Такого быть не может, пусть ts будет доволен
  if (search === null) {
    return null;
  }

  const sectionName = sectionModel?.data?.name;
  const folderName = folderModel?.data?.name;

  const entityText = sectionName
    ? 'Совпадения в текущем разделе:'
    : folderName
    ? 'Совпадения в текущей папке:'
    : null;
  const entityName = sectionName || folderName || null;

  return (
    <PageInnerContainer sx={{ gap: sp.s120 }}>
      {(mobxStore.searchStore.results?.results_from_entity?.length &&
        entityText &&
        entityName && (
          <EntitySearchResults
            sort={sort}
            amount={mobxStore.searchStore.fromEntityResultsCount || 0}
            results={mobxStore.searchStore.results.results_from_entity}
            entityText={entityText}
            entityName={entityName}
          ></EntitySearchResults>
        )) ||
        null}

      {(mobxStore.searchStore.results?.other_results?.length && sort && (
        <CommonSearchResults
          sort={sort}
          amount={mobxStore.searchStore.commonResultsCount || 0}
          onSortingChange={onSortingChange}
          results={mobxStore.searchStore.results.other_results}
          isOther={Boolean(mobxStore.searchStore.results?.results_from_entity)}
        ></CommonSearchResults>
      )) ||
        null}

      {!mobxStore.searchStore.results?.results_from_entity?.length &&
      mobxStore.searchStore.results?.other_results?.length === 0 ? (
        <>
          <Typography sx={{ fontWeight: 'bold' }} variant="f30">
            Ничего не найдено
          </Typography>
          <SecondaryButton
            onClick={() =>
              navigate({
                pathname: mobxStore.uiStore.lastPathnameBeforeSearch,
                search: mobxStore.uiStore.lastSearchParamsBeforeSearch,
              })
            }
            sx={{ width: 'min-content', px: 10 }}
          >
            Сбросить поиск
          </SecondaryButton>
        </>
      ) : null}
    </PageInnerContainer>
  );
});
